import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import DualCalendarDropdown from '../../../shared/components/ui/DualCalendarDropdown';
import CreateMatchModal from './CreateMatchModal';
import DeleteAlert from '../../../shared/components/ui/DeleteAlert';
import Toast from '../../../shared/components/ui/Toast';
// import Button from '../../../shared/components/ui/Button'; // Hidden for now
import MatchCard from '../../../shared/components/ui/MatchCard';
import HorizontalScrollContainer from '../../../shared/components/ui/HorizontalScrollContainer';
import VideoLinkModal from '../../../shared/components/ui/VideoLinkModal';
import { useMatches } from '../../../shared/hooks/useMatches';
import { useMatchVideos } from '../../../shared/hooks/useMatchVideos';
import { useFavorites } from '../../../shared/contexts/FavoritesContext';
import { useMobile } from '../../../shared/hooks/useMobile';
import { supabase } from '../../../shared/lib/supabase';
import { transformMatchToUI } from '../../../shared/utils/dataTransformers';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { useStatusFilter } from '../../../shared/hooks/useStatusFilter';
import StatusFilterDropdown, { type BasicStatus } from '../../../shared/components/ui/StatusFilterDropdown';
import ViewModeToggle, { type ViewMode } from '../../../shared/components/ui/ViewModeToggle';
import SortModeToggle, { type SortMode } from '../../../shared/components/ui/SortModeToggle';
import MatchListRow from '../../../shared/components/ui/MatchListRow';

// Reusable loading component for consistency
const TabLoader: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center py-8">
    <div className="flex items-center space-x-2">
      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <span className="text-sm text-gray-600">{message}</span>
    </div>
  </div>
);

interface Match {
  id: string;
  name: string;
  date: string;
  time: string;
  tournament: {
    id: string;
    name: string;
    logo: string;
  };
  homeTeam: {
    name: string;
    logo: string;
    score: number;
  };
  awayTeam: {
    name: string;
    logo: string;
    score: number;
  };
  isFavorite: boolean;
  venue: string;
  status: 'upcoming' | 'live' | 'finished';
  thumbnailUrl?: string;
  duration?: string;
  videoCount?: number;
  videoUrl?: string;
}

interface MatchSelectionScreenProps {
  isMobile?: boolean;
  onToggleSidebar?: () => void;
}

const MatchSelectionScreen: React.FC<MatchSelectionScreenProps> = ({ isMobile = false, onToggleSidebar }) => {
  const [activeTopTab, setActiveTopTab] = useState<'all' | 'favourites'>('all');
  const [favoritesVersion, setFavoritesVersion] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [sortMode, setSortMode] = useState<SortMode>('relevant');
  const [statusSelection, setStatusSelection] = useState<BasicStatus[]>(['upcoming', 'live', 'completed']);
  const { isMobile: detectedMobile } = useMobile();
  const actualIsMobile = isMobile || detectedMobile;

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isVideoLinkModalOpen, setIsVideoLinkModalOpen] = useState(false);
  const [selectedMatchForEdit, setSelectedMatchForEdit] = useState<any>(null);
  const [selectedMatchForDelete, setSelectedMatchForDelete] = useState<any>(null);
  const [selectedMatchForVideoLink, setSelectedMatchForVideoLink] = useState<any>(null);
  const [toast, setToast] = useState<{isVisible: boolean, type: 'success' | 'error' | 'info', message: string}>({
    isVisible: false,
    type: 'success',
    message: ''
  });
  const dateButtonRef = useRef<HTMLButtonElement>(null);

  // Use real data from database
  const { matches: dbMatches, loading, error, loadMatches } = useMatches();
  const { favoriteTournaments } = useFavorites();
  
  // Get match IDs for fetching video data - but don't auto-fetch
  const matchIds = dbMatches ? dbMatches.map(match => match.id) : [];
  const { getMatchThumbnail, getMatchVideoCount, fetchMatchVideos } = useMatchVideos([]);
  
  // Force re-render when favorites change
  useEffect(() => {
    setFavoritesVersion(prev => prev + 1);
  }, [favoriteTournaments]);

  // Fetch thumbnails on page refresh (only once)
  useEffect(() => {
    if (dbMatches && dbMatches.length > 0) {
      // Only fetch if we don't have cached data
      const hasCachedData = dbMatches.some(match => getMatchThumbnail(match.id) !== null);
      if (!hasCachedData) {
        fetchMatchVideos(matchIds);
      }
    }
  }, [dbMatches, getMatchThumbnail, fetchMatchVideos, matchIds]); // Only run when dbMatches changes (page refresh)

  // Time difference calculation for sorting
  const getMatchDateTime = (m: Match): number => {
    const dt = new Date(`${m.date} ${m.time}`);
    const t = dt.getTime();
    return isNaN(t) ? Date.now() : t;
  };

  const getTimeDifference = (match: Match): number => {
    const now = Date.now();
    const matchTime = getMatchDateTime(match);
    return Math.abs(now - matchTime);
  };

  const normalizeStatus = (s?: string): BasicStatus | undefined => {
    if (!s) return undefined;
    if (s === 'finished' || s === 'completed') return 'completed';
    if (s === 'upcoming' || s === 'live') return s;
    return undefined;
  };

  // Unified sort metric based on selected sort mode
  const getSortMetric = (match: Match): number => {
    const timestamp = getMatchDateTime(match);
    switch (sortMode) {
      case 'date_desc':
        return -timestamp;
      case 'date_asc':
        return timestamp;
      case 'relevant':
      default:
        return getTimeDifference(match);
    }
  };

  // Transform database matches to UI format
  const matches: Match[] = dbMatches ? dbMatches.map(match => {
    const realThumbnail = getMatchThumbnail(match.id);
    const videoCount = getMatchVideoCount(match.id);
    
    return {
      ...transformMatchToUI(match),
      isFavorite: false, // Removed match favorites functionality
      thumbnailUrl: realThumbnail || undefined, // Use real thumbnail or no thumbnail
      duration: match.status === 'finished' ? '90:00' : undefined, // Sample duration for completed matches
      videoCount: videoCount > 0 ? videoCount : undefined // Only add video count if > 0
    };
  }) : [];

  const navigate = useNavigate();
  const location = useLocation();

  const handleMatchClick = (matchId: string) => {
    const orgIdFromPath = extractOrgIdFromPath(location.pathname);
    const orgId = orgIdFromPath || 'default';
    navigate(`/o/${orgId}/replay/${matchId}#videos`);
  };

  const handleEditMatch = (e: React.MouseEvent, match: any) => {
    e.stopPropagation();
    // Find the original database match by ID
    const originalMatch = dbMatches?.find(dbMatch => dbMatch.id === match.id);
    setSelectedMatchForEdit(originalMatch);
    setIsEditModalOpen(true);
  };

  const handleDeleteMatch = (e: React.MouseEvent, match: any) => {
    e.stopPropagation();
    setSelectedMatchForDelete(match);
    setIsDeleteAlertOpen(true);
  };

  const handleLinkVideo = (e: React.MouseEvent, match: any) => {
    e.stopPropagation();
    setSelectedMatchForVideoLink(match);
    setIsVideoLinkModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedMatchForDelete) return;
    
    try {
      const { error } = await supabase
        .from('matches')
        .update({ active: false })
        .eq('id', selectedMatchForDelete.id);

      if (error) {
        console.error('Error deactivating match:', error);
        setToast({
          isVisible: true,
          type: 'error',
          message: 'Failed to delete match. Please try again.'
        });
        return;
      }

      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Match deleted successfully!'
      });
      
      // Refresh the matches list to reflect the deletion
      await loadMatches();
      
    } catch (err) {
      console.error('Error deactivating match:', err);
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Failed to delete match. Please try again.'
      });
    } finally {
      setIsDeleteAlertOpen(false);
      setSelectedMatchForDelete(null);
    }
  };

  const handleVideoLinkSuccess = async (videoUrls: string[]) => {
    if (selectedMatchForVideoLink) {
      // In a real app, this would update the match in the database
      console.log('Videos linked:', videoUrls, 'for match:', selectedMatchForVideoLink.id);
      
      // Fetch thumbnails for this specific match only
      await fetchMatchVideos([selectedMatchForVideoLink.id]);
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: `${videoUrls.length} video${videoUrls.length > 1 ? 's' : ''} linked successfully!`
      });
      
      // Close modal
      setIsVideoLinkModalOpen(false);
      setSelectedMatchForVideoLink(null);
    }
  };

  // Search function to filter matches based on all text fields
  const searchMatches = (matches: Match[], query: string): Match[] => {
    if (!query.trim()) return matches;
    
    const searchTerm = query.toLowerCase().trim();
    
    return matches.filter(match => {
      // Search in match name
      if (match.name?.toLowerCase().includes(searchTerm)) return true;
      
      // Search in tournament name
      if (match.tournament.name?.toLowerCase().includes(searchTerm)) return true;
      
      // Search in team names
      if (match.homeTeam.name?.toLowerCase().includes(searchTerm)) return true;
      if (match.awayTeam.name?.toLowerCase().includes(searchTerm)) return true;
      
      // Search in venue
      if (match.venue?.toLowerCase().includes(searchTerm)) return true;
      
      // Search in date (formatted)
      const formattedDate = new Date(match.date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      if (formattedDate.toLowerCase().includes(searchTerm)) return true;
      
      // Search in time
      if (match.time?.toLowerCase().includes(searchTerm)) return true;
      
      // Search in status
      if (match.status?.toLowerCase().includes(searchTerm)) return true;
      
      return false;
    });
  };

  const openCalendar = () => {
    setIsCalendarOpen(!isCalendarOpen);
  };

  const handleDateRangeSelect = (startDate: Date, endDate: Date) => {
    setSelectedDate(startDate);
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    setIsCalendarOpen(false);
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
    setSelectedStartDate(null);
    setSelectedEndDate(null);
  };

  // Check if a match is within the selected date range
  const isMatchInSelectedRange = (match: Match) => {
    const matchDate = new Date(match.date);
    
    // If we have a date range
    if (selectedStartDate && selectedEndDate) {
      return matchDate >= selectedStartDate && matchDate <= selectedEndDate;
    }
    
    // If we only have a single date
    if (selectedDate) {
      return matchDate.toDateString() === selectedDate.toDateString();
    }
    
    return true; // Show all matches if no date is selected
  };

  // NOTE: Hooks must be declared before any early returns. Early returns are moved below.

  // Filter matches based on favorites, selected date range, and search query
  const preStatusFiltered = searchMatches(matches, searchQuery)
    .filter(match => {
    // Filter by favorites tab
    if (activeTopTab === 'favourites') {
      const isFavoriteTournament = favoriteTournaments.includes(match.tournament.id);
      if (!isFavoriteTournament) return false;
    }
    
    // Filter by selected date range
    if (!isMatchInSelectedRange(match)) return false;
    
      return true;
    })
  ;

  // Status filter with URL sync and counts
  const { status, counts } = useStatusFilter(preStatusFiltered);

  const activeStatuses: BasicStatus[] =
    status === 'all'
      ? statusSelection
      : (status === 'upcoming' || status === 'live' || status === 'completed'
          ? [status]
          : statusSelection);

  const filteredMatches = preStatusFiltered.filter((match) => {
    const s = normalizeStatus(match.status);
    if (!s) return false;
    if (activeStatuses.length === 3) return true;
    return activeStatuses.includes(s);
  });

  // Apply selected sort mode
  const sortedMatches = [...filteredMatches].sort((a, b) => getSortMetric(a) - getSortMetric(b));
  

  // Early returns after hooks to satisfy rules-of-hooks
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <TabLoader message="Loading matches..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Matches</h2>
          <p className="text-gray-600 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  // Force re-computation when favorites change (removed searchQuery to prevent focus loss)
  const key = `${activeTopTab}-${favoritesVersion}`;

  // Group matches by tournament and sort by time difference
  const groupMatchesByTournament = (matchList: Match[]) => {
    const grouped: { [key: string]: Match[] } = {};
    
    matchList.forEach(match => {
      const tournamentId = match.tournament.id;
      if (!grouped[tournamentId]) {
        grouped[tournamentId] = [];
      }
      grouped[tournamentId].push(match);
    });
    
    // Sort matches within each tournament by time difference
    Object.keys(grouped).forEach(tournamentId => {
      grouped[tournamentId].sort((a, b) => {
        const diffA = getTimeDifference(a);
        const diffB = getTimeDifference(b);
        return diffA - diffB;
      });
    });
    
    return grouped;
  };

  // Group matches by team and sort by time difference
  const groupMatchesByTeam = (matchList: Match[]) => {
    const grouped: { [key: string]: Match[] } = {};
    
    matchList.forEach(match => {
      // Group by home team
      const homeTeamId = match.homeTeam.name;
      if (!grouped[homeTeamId]) {
        grouped[homeTeamId] = [];
      }
      grouped[homeTeamId].push(match);
    });
    
    // Sort matches within each team by time difference
    Object.keys(grouped).forEach(teamId => {
      grouped[teamId].sort((a, b) => {
        const diffA = getTimeDifference(a);
        const diffB = getTimeDifference(b);
        return diffA - diffB;
      });
    });
    
    return grouped;
  };

  // Group filtered matches by tournament
  const groupedMatches = groupMatchesByTournament(sortedMatches);
  
  // Sort tournament sections by current sort metric
  const sortedTournaments = Object.entries(groupedMatches).sort(([, matchesA], [, matchesB]) => {
    const metricA = Math.min(...matchesA.map(match => getSortMetric(match)));
    const metricB = Math.min(...matchesB.map(match => getSortMetric(match)));
    return metricA - metricB;
  });
  
  // Group by teams for team sections
  const teamGroupedMatches = groupMatchesByTeam(sortedMatches);
  
  // Sort team sections by current sort metric
  const sortedTeams = Object.entries(teamGroupedMatches).sort(([, matchesA], [, matchesB]) => {
    const metricA = Math.min(...matchesA.map(match => getSortMetric(match)));
    const metricB = Math.min(...matchesB.map(match => getSortMetric(match)));
    return metricA - metricB;
  });

  return (
    <div key={key} className="min-h-screen bg-gray-50">
      {/* Custom Header with Search - Sticky */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Left Side - Mobile Menu Button + Tabs */}
          <div className="flex items-center space-x-2 sm:space-x-8">
            {/* Mobile Menu Button */}
            {actualIsMobile && onToggleSidebar && (
              <button
                onClick={onToggleSidebar}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-44 min-w-44"
                title="Open menu"
                aria-label="Open menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}

            {/* Tabs */}
            <div className="flex space-x-4 sm:space-x-8">
              <button
                onClick={() => setActiveTopTab('all')}
                className={`pb-2 px-1 border-b-2 font-medium text-xs sm:text-sm min-h-44 ${
                  activeTopTab === 'all'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveTopTab('favourites')}
                className={`pb-2 px-1 border-b-2 font-medium text-xs sm:text-sm min-h-44 ${
                  activeTopTab === 'favourites'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Favourites
              </button>
            </div>
          </div>

          {/* Center - Search Bar */}
          <div className="flex-1 flex justify-center">
            <div className="relative w-full max-w-md">
              <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={actualIsMobile ? "Search..." : "Search matches, teams, tournaments..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-8 sm:pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center min-h-44 min-w-44"
                >
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Right Side - Actions - Hidden on Mobile */}
          {!actualIsMobile && (
          <div className="flex items-center space-x-3">
            <ViewModeToggle value={viewMode} onChange={setViewMode} aria-label="Match view" />
            <SortModeToggle value={sortMode} onChange={setSortMode} aria-label="Sort matches" />
            {/* Status Filter */}
            <StatusFilterDropdown
              selected={statusSelection}
              onChange={setStatusSelection}
              counts={counts}
            />
            {/* Date Range Selector */}
            <div className="relative">
              <button 
                ref={dateButtonRef}
                onClick={openCalendar}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer flex items-center justify-center"
                title={selectedStartDate && selectedEndDate 
                  ? `${selectedStartDate.getDate().toString().padStart(2, '0')}/${(selectedStartDate.getMonth() + 1).toString().padStart(2, '0')} - ${selectedEndDate.getDate().toString().padStart(2, '0')}/${(selectedEndDate.getMonth() + 1).toString().padStart(2, '0')}`
                  : 'Select Date Range'
                }
              >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              </button>
              
              {/* Dual Calendar Dropdown */}
              {isCalendarOpen && (
                <DualCalendarDropdown
                  isOpen={isCalendarOpen}
                  onClose={() => setIsCalendarOpen(false)}
                  onDateRangeSelect={handleDateRangeSelect}
                  onClearFilter={clearDateFilter}
                  selectedStartDate={selectedStartDate || undefined}
                  selectedEndDate={selectedEndDate || undefined}
                  triggerRef={dateButtonRef}
                />
              )}
            </div>

            {/* Create Match Button - Hidden for now */}
            {/* <Button
              onClick={() => setIsCreateModalOpen(true)}
              variant="primary"
              size="md"
            >
              + Match
            </Button> */}
            </div>
          )}
        </div>
      </div>

      {/* Matches Grid */}
      <div className="p-6">
        {filteredMatches.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">
              {searchQuery ? '🔍' : '🔄'}
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No matches found' : 'No replays available'}
            </h3>
            <p className="text-gray-500">
              {searchQuery 
                ? `No matches found for "${searchQuery}". Try a different search term.`
                : 'Replays will appear here once matches are completed.'
              }
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Clear search
              </button>
            )}
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-6">
            {sortedTournaments.map(([tournamentId, tournamentMatches]) => {
              const firstMatch = tournamentMatches[0];
              return (
                <div key={tournamentId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center space-x-2">
                    <img
                      src={firstMatch.tournament.logo}
                      alt=""
                      className="w-5 h-5 rounded object-cover"
                      onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/24x24/cccccc/666666?text=⚽'; }}
                    />
                    <span className="text-sm font-semibold text-gray-900">{firstMatch.tournament.name}</span>
                    <span className="text-xs text-gray-500">({tournamentMatches.length} match{tournamentMatches.length !== 1 ? 'es' : ''})</span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {tournamentMatches.map((match) => (
                      <MatchListRow
                        key={match.id}
                        match={match}
                        onMatchClick={handleMatchClick}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-8">
            {/* All Matches Section */}
            <HorizontalScrollContainer
              title="All"
              subtitle={`${filteredMatches.length} match${filteredMatches.length !== 1 ? 'es' : ''}`}
              icon="⚽"
            >
              {filteredMatches.map((match) => (
                <div key={match.id} className="flex-shrink-0 w-80">
                  <MatchCard
                    match={match}
                    onMatchClick={handleMatchClick}
                    onEditMatch={handleEditMatch}
                    onDeleteMatch={handleDeleteMatch}
                    onLinkVideo={handleLinkVideo}
                  />
                </div>
              ))}
            </HorizontalScrollContainer>

            {/* Tournament Sections */}
            {sortedTournaments.map(([tournamentId, tournamentMatches]) => {
              const firstMatch = tournamentMatches[0];
              return (
                <HorizontalScrollContainer
                  key={tournamentId}
                  title={firstMatch.tournament.name}
                  subtitle={`${tournamentMatches.length} match${tournamentMatches.length !== 1 ? 'es' : ''}`}
                  icon={
                      <img 
                        src={firstMatch.tournament.logo} 
                        alt={`${firstMatch.tournament.name} logo`}
                      className="w-6 h-6 rounded object-cover"
                        onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/24x24/cccccc/666666?text=⚽';
                      }}
                    />
                  }
                >
                    {tournamentMatches.map((match) => (
                    <div key={match.id} className="flex-shrink-0 w-80">
                    <MatchCard
                      match={match}
                      onMatchClick={handleMatchClick}
                      onEditMatch={handleEditMatch}
                      onDeleteMatch={handleDeleteMatch}
                      onLinkVideo={handleLinkVideo}
                    />
                    </div>
                  ))}
                </HorizontalScrollContainer>
              );
            })}

            {/* Team Sections */}
            {sortedTeams.map(([teamName, teamMatches]) => (
              <HorizontalScrollContainer
                key={teamName}
                title={teamName}
                subtitle={`${teamMatches.length} match${teamMatches.length !== 1 ? 'es' : ''}`}
                icon={
                  <img
                    src={teamMatches[0]?.homeTeam?.logo}
                    alt={`${teamName} logo`}
                    className="w-6 h-6 rounded object-cover"
                    onError={(e) => {
                      e.currentTarget.src = 'https://via.placeholder.com/24x24/cccccc/666666?text=🏟️';
                    }}
                  />
                }
              >
                {teamMatches.map((match) => (
                  <div key={match.id} className="flex-shrink-0 w-80">
                    <MatchCard
                      match={match}
                      onMatchClick={handleMatchClick}
                      onEditMatch={handleEditMatch}
                      onDeleteMatch={handleDeleteMatch}
                      onLinkVideo={handleLinkVideo}
                              />
                  </div>
                ))}
              </HorizontalScrollContainer>
            ))}
          </div>
        ) }
      </div>

      {/* Create Match Modal */}
      {isCreateModalOpen && (
        <CreateMatchModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            // Show success toast
            setToast({
              isVisible: true,
              type: 'success',
              message: 'Match created successfully!'
            });
            // Refresh the matches list to show the new match
            loadMatches();
          }}
        />
      )}

      {/* Edit Match Modal */}
      {isEditModalOpen && (
        <CreateMatchModal
          isOpen={isEditModalOpen}
          mode="edit"
          matchData={selectedMatchForEdit}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedMatchForEdit(null);
          }}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setSelectedMatchForEdit(null);
            // Show success toast
            setToast({
              isVisible: true,
              type: 'success',
              message: 'Match details successfully updated!'
            });
            // Refresh the matches list to show updated data
            loadMatches();
          }}
        />
      )}

      {/* Delete Alert */}
      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => {
          setIsDeleteAlertOpen(false);
          setSelectedMatchForDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete"
        description="Are you sure you want to delete this match? It will be hidden from the match listing."
      />

      {/* Video Link Modal */}
      <VideoLinkModal
        isOpen={isVideoLinkModalOpen}
        onClose={() => {
          setIsVideoLinkModalOpen(false);
          setSelectedMatchForVideoLink(null);
        }}
        onSuccess={handleVideoLinkSuccess}
        matchName={selectedMatchForVideoLink?.name || `${selectedMatchForVideoLink?.homeTeam?.name} vs ${selectedMatchForVideoLink?.awayTeam?.name}`}
        matchId={selectedMatchForVideoLink?.id}
      />

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
        type={toast.type}
        message={toast.message}
        duration={5000}
      />
    </div>
  );
};

export default MatchSelectionScreen;
